/** Virtual "other" school helpers: detect the sentinel id and append the select option. */

import type { SelectOption } from '@/components/forms/Select';
import {
  CATALOG_ESTABLISHMENT_ID_RE,
  OTHER_ESTABLISHMENT_ID,
  OTHER_ESTABLISHMENT_IDS,
  OTHER_ESTABLISHMENT_LABEL,
} from '@/constants/shared-catalog';

/** Lookup of "other" school aliases (`autre` / `other`), case-insensitive after trim. */
const OTHER_ESTABLISHMENT_ID_SET: ReadonlySet<string> = new Set(OTHER_ESTABLISHMENT_IDS);

/** True when the value is the virtual "other" school (`autre` / `other`). */
export function isOtherEstablishmentId(value: string | undefined | null): boolean {
  return !!value && OTHER_ESTABLISHMENT_ID_SET.has(value.trim().toLowerCase());
}

/** True when the value is a catalog establishment UUID. */
export function isCatalogEstablishmentId(value: string | undefined | null): boolean {
  return !!value && CATALOG_ESTABLISHMENT_ID_RE.test(value);
}

/** `{ establishmentId, establishmentName }` for a school select value (other or catalog). */
export function schoolEstablishmentRefFromId(
  id: string,
  existingName?: string,
): { establishmentId: string; establishmentName: string } {
  if (isOtherEstablishmentId(id)) {
    const name = existingName?.trim();
    return {
      establishmentId: OTHER_ESTABLISHMENT_ID,
      establishmentName: name && !isOtherEstablishmentId(name) ? name : OTHER_ESTABLISHMENT_LABEL,
    };
  }
  return { establishmentId: id, establishmentName: existingName?.trim() ?? '' };
}

/** Append the "other" option when the list does not already include it. */
export function appendOtherEstablishmentOption(options: SelectOption[]): SelectOption[] {
  if (options.some((option) => isOtherEstablishmentId(option.value))) return options;
  return [...options, { value: OTHER_ESTABLISHMENT_ID, label: OTHER_ESTABLISHMENT_LABEL }];
}
